import { useState, useCallback, useRef } from "react";
import { commonPostApi } from "../../utils/apiService.js";
import { API_ENDPOINTS } from "../../utils/apiEndpoints";
import { showToast } from "../../components/common/toast.js";
import { TOAST_TYPE, COMMON_MSG, FEATURE_MSG } from "../../constants/toast";

/**
 * Custom hook for managing role permissions
 * Handles fetching all permissions, assigned permissions, and adding permissions to roles
 * 
 * @returns {Object} Permissions list, available permissions, assigned permissions, loading state, and functions
 */
const useRolePermissions = () => {
  const [permissionsList, setPermissionsList] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [assignedPermissions, setAssignedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchingAllRef = useRef(false);

  const fetchAllPermissionsList = useCallback(async () => {
    // Prevent concurrent calls
    if (fetchingAllRef.current) {
      return Promise.resolve();
    }

    fetchingAllRef.current = true;
    try {
      const allPermissionsResponse = await commonPostApi(API_ENDPOINTS.GET_ALL_PERMISSIONS);
      const allPermissions = allPermissionsResponse?.data?.data?.map((permission) => ({
        ...permission,
        name: permission?.permission_name,
      })) || [];

      setPermissionsList(allPermissions);
      setAvailablePermissions(allPermissions);
    } catch (error) {
      console.error("Error fetching all permissions:", error);
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
    } finally {
      fetchingAllRef.current = false;
    }
  }, []);

  const fetchAssignedPermissions = useCallback(async (roleId) => {
    if (loading) return;
    
    setLoading(true);
    
    try {
      // Ensure permissionsList is populated before proceeding
      let currentPermissionsList = permissionsList;
      if (!currentPermissionsList || currentPermissionsList.length === 0) {
        // Fetch all permissions first if not loaded
        const allPermissionsResponse = await commonPostApi(API_ENDPOINTS.GET_ALL_PERMISSIONS);
        currentPermissionsList = allPermissionsResponse?.data?.data?.map((permission) => ({
          ...permission,
          name: permission?.permission_name,
        })) || [];
        setPermissionsList(currentPermissionsList);
        setAvailablePermissions(currentPermissionsList);
      }
      
      // Fetch assigned permissions for the role
      const response = await commonPostApi(API_ENDPOINTS.GET_PERMISSIONS_TO_ROLE, {
        app_role_id: roleId
      });
      
      const assignedIds = response?.data?.data?.map(item => item?.app_permission_id) || [];
      const assigned = response?.data?.data || [];
      
      setAvailablePermissions(
        currentPermissionsList.filter(permission =>
          !assignedIds.includes(permission.app_permission_id)
        )
      );
      setAssignedPermissions(assigned);
    } catch (error) {
      console.error("Error fetching assigned permissions:", error);
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
    } finally {
      setLoading(false);
    }
  }, [loading, permissionsList]);

  const addPermissionsToRole = useCallback(async (roleId, permissionIds) => {
    if (loading) return;
    setLoading(true);
    
    try {
      const response = await commonPostApi(API_ENDPOINTS.ADD_PERMISSIONS_TO_ROLE, {
        app_role_id: roleId,
        app_permission_ids: permissionIds
      });
      
      if (response?.data?.message === "Success") {
        showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.ROLE.PERMISSION_ADDED);
        return true;
      } else {
        showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
        return false;
      }
    } catch (error) {
      console.error("Error adding permissions:", error);
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
      return false;
    } finally {
      setLoading(false);
    }
  }, [loading]);

  return {
    permissionsList,
    availablePermissions,
    setAvailablePermissions,
    assignedPermissions,
    setAssignedPermissions,
    loading: loading,
    fetchAllPermissionsList,
    fetchAssignedPermissions,
    addPermissionsToRole,
  };
};

export default useRolePermissions;
